/* eslint-disable jsx-a11y/anchor-has-content */
import { ArrowSmallRightIcon } from '@heroicons/react/24/solid'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ColumnDef } from '@tanstack/react-table'
import classNames from 'classnames'
import { PageEdit, Phone } from 'iconoir-react'
import { compact, debounce, uniqBy, extend, defer } from 'lodash'
import moment from 'moment'
import { useEffect, useMemo, useRef, useState } from 'react'
import Button from '../Button'
import CircularProgressBar from '../CircularProgressBar'
import { useConfirmation } from '../ConfirmationContext'
import { ControlledSearch } from 'app/components/ui/controlled-search'
import { DateComponent } from '../DateComponent'
import TrackableIcon from '../Icon/TrackableIcon'
import { LazyLocationCell } from '../LazyLocationCell'
import PhoneNumber from '../PhoneNumber'
import { ContactCell } from '../shared/ContactCell'
import Spinner from '../Spinner'
import SimpleTable from '../table/SimpleTable'
import { Toggle } from '../Toggle'
import { PersonOutcomes } from './PersonOutcomes'
import { getAge } from 'app/date.utils'
import useFakeProgress from 'app/hooks/fakeProgress'
import type { ActivityJson } from 'app/types/entities'
import type { CallJson, PersonSummary } from 'app/types/entities'
import type { ContactJson } from 'app/types/entities'
import type { FormJson } from 'app/types/entities'
import { closeModal } from 'app/lib/modal'
import { graphql } from 'app/generated/gql'
import { gqlMutation } from 'app/lib/graphqlClient'

const REMATCH_ACTIVITY_MUTATION = graphql(`
  mutation RematchActivity($id: ID!, $contactId: String) {
    rematchActivity(id: $id, contactId: $contactId) {
      id
    }
  }
`)
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { useContactStore } from 'app/stores/StoreContext'
import { useUser, useWorkspace } from 'app/utils'
import { bg } from 'lib/background'
import { debug, logError } from 'lib/logging'
import { lastNameInitial } from 'lib/nameUtils'
import { buildMatchActivityContactSearch } from 'lib/matchActivityContactSearch'

function toTitleCase(str: string | null | undefined = ``): string {
  return (str ?? ``)
    .replace(`,`, `, `)
    .split(` `)
    .map((word) => word.toLowerCase().replace(/^./, (c) => c.toUpperCase()))
    .join(` `)
}

export function MatchActivityModal({
  call,
  form,
  activity,
}: {
  call?: CallJson
  form?: FormJson
  activity?: ActivityJson
}) {
  const navigate = useNavigate()
  const user = useUser()
  const workspace = useWorkspace()
  const contactStore = useContactStore()

  // Derive people list and location from either call or form
  const isForm = !!form && !call
  const people: PersonSummary[] = useMemo(() => {
    if (call?.ai?.people?.length) return call.ai.people
    if (form) {
      return [
        {
          name: form.patientName || `Unknown`,
          phone: form.phone,
        } as PersonSummary,
      ]
    }
    return []
  }, [call, form])
  const recordLocationId = call?.locationId || form?.locationId || activity?.locationId
  const recordDate = call?.callDate || (form?.submissionDate as string | undefined)
  const [selectedPerson, setSelectedPerson] = useState<PersonSummary | null>(null)
  const [selectedPersonIndex, setSelectedPersonIndex] = useState<number | null>(null)
  const [selectedContact, setSelectedContact] = useState<ContactJson | null>(null)
  const [matchedContact, setMatchedContact] = useState<ContactJson | null>(null)
  const [contacts, setContacts] = useState<ContactJson[]>()
  const [topMatches, setTopMatches] = useState<ContactJson[]>([])
  const [page, setPage] = useState<number>(0)
  const [inProgress, setInProgress] = useState(false)
  const [confirm] = useConfirmation()
  const [progress, progressStart, progressEnd] = useFakeProgress()
  const [searchValue, setSearchValue] = useState(``)
  const [isLoading, setIsLoading] = useState(false)

  const [currentPage, setCurrentPage] = useState<`contacts` | `activities`>(`activities`)

  const selectedActivityContact = useMemo(() => {
    debug(
      `holla`,
      `select ActivityContact`,
      selectedPersonIndex,
      activity,
      !!selectedPersonIndex,
      !activity,
      !!selectedPersonIndex || !activity,
    )
    if (selectedPersonIndex === null || !activity) return undefined
    debug(`holla`, `selecting`)
    return activity.contacts?.find((c) => c.peopleIndex === selectedPersonIndex)
  }, [activity, selectedPerson, selectedPersonIndex])
  debug(`holla`, `selectedActivityContact`, selectedActivityContact)

  const defaultColumns = (
    selectedContact: ContactJson | null,
    matchedContact: ContactJson | null,
    onContactHandler: (contact: ContactJson) => void,
    onSelectContact: (contact: ContactJson) => void,
  ): ColumnDef<ContactJson & { matchTypes?: string[] }, any>[] =>
    compact([
      {
        accessorFn: (row) => row.id,
        id: `actions`,
        header: ``,
        maxSize: 50,
        enableResizing: false,
        cell: function Actions({ row }) {
          useEffect(() => {
            if (matchedContact?.id === row.original.id) {
              row.pin(`top`)
            }
          }, [])
          return (
            <div className="flex items-center justify-center">
              <input
                type="radio"
                checked={selectedContact?.id === row.original.id}
                onChange={() => onSelectContact(row.original)}
                // disabled={matchedContact?.id === row.original.id ? true : undefined}
                className="h-4 w-4 border-strong text-blue-600 focus:ring-0 disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
          )
        },
        meta: {
          defaultVisible: true,
          pinned: false,
          tooltipClassName: `w-full`,
        },
        enableSorting: false,
        enableHiding: false,
        size: 50,
        minSize: 50,
      },
      // user.type === `super` && {
      //   id: `id`,
      //   accessorKey: `id`,
      //   header: `ID`,
      //   meta: { group: `General`, defaultVisible: true, pinned: false, sortKey: `id.keyword` },
      // },
      {
        id: `name`,
        accessorFn: (row) => row.name,
        header: `Name`,
        enableResizing: true,
        meta: {
          group: `General`,
          defaultVisible: true,
          pinned: false,
          sortKey: `name.plain`,
          onClick: (row) => onContactHandler(row),
          tooltipText: (row) => row.name ?? ``,
        },
        enableHiding: false,
        minSize: 100,
        cell: (props) => <ContactCell contact={props.row.original} matchedContact={matchedContact} />,
      },
      {
        id: `Patient Linked`,
        accessorKey: `patientIds`,
        header: `Patient Linked`,
        meta: {
          defaultVisible: true,
          sortKey: `patientIds`,
        },
        cell: (props) => (
          <span
            className={classNames(
              `flex h-6 items-center rounded border px-1.5 text-xs font-medium not-italic leading-4`,
              props.getValue() ? `bg-green-100 text-green-700` : `bg-red-100 text-red-700`,
            )}
          >
            {props.getValue() ? `Linked` : `Not Linked`}
          </span>
        ),
      },
      {
        id: `locationId`,
        accessorKey: `locationId`,
        header: `Location`,
        cell: ({ row }) => <LazyLocationCell locationId={row.original.locationId!} />,
        meta: {
          defaultVisible: true,
        },
        enableSorting: false,
      },
      user?.type === `super` && {
        id: `matchTypes`,
        accessorKey: `matchTypes`,
        header: `Match Types`,
        meta: { group: `General`, defaultVisible: true, pinned: false, sortKey: `matchTypes.keyword` },
      },
      {
        id: `homePhone`,
        accessorKey: `homePhone`,
        header: `Home Phone`,
        meta: {
          group: `Communication`,
          defaultVisible: true,
          pinned: false,
          sortKey: `homePhone.keyword`,
        },
        cell: (props) => <PhoneNumber number={props.row.original.homePhone} />,
      },
      {
        id: `cellPhone`,
        accessorKey: `cellPhone`,
        header: `Cell Phone`,
        cell: (props) => <PhoneNumber number={props.row.original.cellPhone} />,
        meta: {
          group: `Communication`,
          defaultVisible: true,
          pinned: false,
          sortKey: `cellPhone.keyword`,
        },
      },
      {
        id: `firstAppointmentDate`,
        accessorKey: `firstAppointmentDate`,
        header: `First Appointment Date`,
        cell: (props) => <DateComponent value={props.row.original.firstAppointmentDate} dateOnly />,
        meta: {
          group: `PMS`,
          defaultVisible: true,
          sortKey: `firstAppointmentDate`,
        },
      },
      {
        id: `lastAppointmentDate`,
        accessorKey: `lastAppointmentDate`,
        header: `Last Appointment Date`,
        cell: (props) => <DateComponent value={props.row.original.lastAppointmentDate} dateOnly />,
        meta: {
          group: `PMS`,
          defaultVisible: true,
          sortKey: `lastAppointmentDate`,
        },
      },
    ])

  useEffect(() => {
    if (window.location.pathname === `/contacts`) {
      setCurrentPage(`contacts`)
    }
  }, [])

  debug(
    `rematch`,
    `selected index`,
    selectedActivityContact?.id,
    activity?.contacts?.findIndex((c) => c.id === selectedActivityContact?.id),
  )

  const data = useMemo(
    () =>
      searchValue !== ``
        ? uniqBy(compact([matchedContact, ...(contacts || [])]), `id`)
        : uniqBy(compact([...topMatches]), `id`),
    [searchValue, contacts, topMatches, matchedContact],
  )
  const columns = useMemo(
    () =>
      defaultColumns(
        selectedContact,
        matchedContact,
        (contact) => {
          const url = `/contacts?tab=all&drawer=${contact.id}&query=${contact.name}&wId=${workspace?.id ?? null}`
          window.open(url, `_blank`)
        },
        setSelectedContact,
      ),
    [selectedContact, matchedContact],
  )

  const [, setSearchParams] = useSearchParams()
  const [showConfirmationStep, setShowConfirmationStep] = useState(false)
  const [showNewContactStep, setShowNewContactStep] = useState(false)
  const [newContactId, setNewContactId] = useState<string>()
  const aiOutcomeContainer = useRef<HTMLDivElement>(null)
  const [searchWorkspace, setSearchWorkspace] = useState(false)

  useEffect(() => {
    if (inProgress) {
      progressStart()
    }
  }, [inProgress])

  // make sure we have a person selected on first load
  useEffect(() => {
    debug(`holla`, `select person useEffect`, call, form, selectedPerson)
    if (people.length && selectedPerson === null) {
      setSelectedPerson(people[0])
      setSelectedPersonIndex(0)
    }
  }, [call, form])

  // when a person is selected, fetch the full contact json for the matching activity contact if it exists
  useEffect(() => {
    if (selectedActivityContact) {
      bg(async () => {
        const result = await contactStore.fetchOne(selectedActivityContact.id)
        const data = (result.data as any)?.contact as ContactJson | undefined
        setMatchedContact(data ?? null)
        setSelectedContact(data ?? null)
      })
    } else {
      setMatchedContact(null)
      setSelectedContact(null)
    }
  }, [selectedActivityContact])

  // when a person is selected, fetch the top matches
  useEffect(() => {
    if (selectedPerson) {
      setIsLoading(true)
      let additionalSearchesString = JSON.stringify({
        firstAppointmentMadeDate: recordDate && moment(recordDate).format(`YYYY-MM-DD`),
        patientCreatedDate: recordDate && moment(recordDate).format(`YYYY-MM-DD`),
        firstAppointmentDate:
          selectedPerson.appointmentDate && moment(selectedPerson.appointmentDate).format(`YYYY-MM-DD`),
        birthDate: selectedPerson.birthDate && moment(selectedPerson.birthDate).format(`YYYY-MM-DD`),
      })
      bg(async () => {
        let data = await window
          .fetch(
            `/api/contacts/matches?name=${selectedPerson.name}&locationId=${recordLocationId}&phone=${
              selectedPerson.phone || call?.callerPhone || ``
            }&additionalSearches=${additionalSearchesString}&limit=5&requireLinkedPatient=false`,
            { credentials: `include` },
          )
          .then((r) => r.json())
        let _topMatches = data?.map((m) => extend(m.contact, { matchTypes: m.matchTypes }))
        debug(`rematch`, `top matches`, data, _topMatches)

        setTopMatches(_topMatches)
        setIsLoading(false)
      })
    }
  }, [selectedPerson, recordLocationId])

  useEffect(() => {
    if (searchValue !== ``) {
      if (!searchWorkspace && !recordLocationId) {
        debug(`rematch`, `skipping search fetch: no recordLocationId`)
        setIsLoading(false)
        return
      }
      bg(async () => {
        const result = await contactStore.fetchList(
          buildMatchActivityContactSearch({
            locationId: recordLocationId,
            workspaceId: activity?.workspaceId,
            searchWorkspace,
            search: searchValue,
            page,
          }),
        )
        const resp = result.data as any
        if (resp?.contacts?.hits) {
          setContacts((data) =>
            uniqBy([...(data || []), ...resp.contacts.hits], `id`).filter((c) => c.id !== matchedContact?.id),
          )
        }
        setIsLoading(false)
      })
    } else {
      setIsLoading(false)
    }
  }, [searchValue, searchWorkspace, page, recordLocationId])

  if (!user) return null

  const onPersonSelect = async (person: PersonSummary, index: number) => {
    if (showConfirmationStep || showNewContactStep) {
      await confirm({
        title: `Switch person`,
        description: `Are you sure you want to loose your unsaved changes?`,
        variant: `danger`,
      }).then(async () => {
        setSelectedPerson(person)
        setSelectedPersonIndex(index)
        setSelectedContact(null)
        setMatchedContact(null)
        setShowConfirmationStep(false)
        setShowNewContactStep(false)
        setSearchValue(``)
        setPage(0)
        setContacts(undefined)
        setInProgress(false)
      })
    } else {
      setSelectedPerson(person)
      setSelectedPersonIndex(index)
    }
  }

  const onSearchValueChange = debounce((val) => {
    setSearchValue(val)
    setPage(0)
    setContacts(undefined)
    setIsLoading(true)
  }, 250)

  const onSearchWorkspaceToggle = (toggle: boolean) => {
    setSearchWorkspace(toggle)
    setPage(0)
    setContacts(undefined)
    setIsLoading(true)
  }

  const mergeContacts = async () => {
    if (!activity?.id) return
    debug(`holla`, `mergeContacts`, selectedActivityContact, selectedContact)
    if (selectedPersonIndex == null) {
      logError(`No selected person index`)
      return
    }
    setInProgress(true)
    const result = await gqlMutation(REMATCH_ACTIVITY_MUTATION, { id: activity?.id, contactId: selectedContact?.id })
    progressEnd()
    if (result.error) {
      notifyError(`Problem with matching activity: ${result.error.message}`)
    } else {
      notifySuccess(`Activity matched to contact successfully`)
    }
  }

  const createNewContact = async () => {
    if (!activity?.id) return
    if (selectedPersonIndex == null) {
      logError(`No selected activity contact`)
      return
    }
    setInProgress(true)
    const result = await gqlMutation(REMATCH_ACTIVITY_MUTATION, { id: activity?.id, contactId: `new` })
    progressEnd()
    if (result.error) {
      notifyError(`Problem with creating a new contact: ${result.error.message}`)
    } else {
      notifySuccess(`New contact created`)
      const newId = (result.data as any)?.rematchActivity?.id
      if (newId) setNewContactId(newId)
    }
  }

  const goToActivity = () => {
    debug(`rematch`, `MatchActivityModal goToActivity`)
    navigate({
      pathname: `/activities`,
      search: `?drawer=${activity?.id}`,
    })
    closeModal()
    defer(() => window.location.reload())
  }

  const goToContact = () => {
    debug(`rematch`, `MatchActivityModal goToContact`, selectedContact?.id)
    setSearchParams((prev) => {
      prev.set(`drawer`, newContactId || selectedContact!.id!)
      return prev
    })
    closeModal()
    setTimeout(() => window.location.reload(), 1000)
  }

  if (!call && !form) return null

  return (
    <div
      id="match-actibity-modal"
      className="flex h-full min-h-0 w-full flex-col overflow-hidden bg-surface-subtle lg:flex-row lg:overflow-visible"
    >
      <div className="w-full shrink-0 border-b border-default p-3 lg:h-auto lg:w-1/4 lg:max-w-xs lg:border-b-0 lg:border-r lg:p-4">
        {isForm ? (
          <p className="mb-4 text-ui-sm">Person from Form</p>
        ) : call?.ai?.people?.length ? (
          <div className="mb-4 flex items-center gap-x-2">
            <span className="text-ui-sm">People identified by</span>
            <TrackableIcon className="h-5 w-5 rounded-md bg-inverse bg-gradient-to-br p-1 text-inverse" />
            <span className="text-ui-sm">A.I.</span>
          </div>
        ) : (
          <p className="mb-4 text-ui-sm">People from Call</p>
        )}

        <div className="-mx-1 flex snap-x gap-2 overflow-x-auto px-1 pb-1 lg:mx-0 lg:flex-col lg:gap-0 lg:overflow-x-visible lg:px-0 lg:pb-0 lg:snap-none">
          {people.map((person, i) => (
            <button
              key={i}
              className={classNames(
                `my-2 flex w-64 shrink-0 snap-start flex-col items-start justify-center rounded-lg bg-surface p-3 text-strong shadow-elevation-2 ring-blue-500 disabled:bg-surface-subtle disabled:text-default lg:w-full lg:shrink lg:p-4`,
                selectedPerson?.name === person.name && `ring-2`,
              )}
              disabled={person !== selectedPerson && isLoading}
              onClick={() => onPersonSelect(person, i)}
            >
              <div className="flex w-full items-center gap-x-1">
                <div className="flex flex-wrap items-center gap-1 text-heading-sm font-normal">
                  <span className="text-left">{person.name ?? (call ? toTitleCase(call.callerId) : `Unknown`)}</span>
                  {!isForm && (
                    <div
                      className={classNames(`flex items-center gap-x-1`, {
                        'text-green-600': person.patientType === `New Patient`,
                        'text-red-600': person.patientType === `Non-Patient`,
                        'text-subtle': [`Current Patient`, `Other`, undefined].includes(person.patientType),
                      })}
                    >
                      <span>·</span>
                      <span>{person.patientType}</span>
                      {person.isCaller && people.length > 1 && (
                        <>
                          <span>·</span>
                          <Phone height={16} width={16} className="text-blue-600" aria-hidden="true" />
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
              {!!person.outcomes && person.outcomes?.length > 0 && (
                <div ref={aiOutcomeContainer} className="mt-1 flex w-full flex-wrap items-center gap-2">
                  <PersonOutcomes person={person} personIndex={i} workspaceId={workspace?.id} />
                </div>
              )}
            </button>
          ))}
        </div>
      </div>
      <div className="flex min-h-0 w-full min-w-0 grow flex-col lg:w-3/4">
        <div className="flex min-h-0 grow flex-col overflow-y-auto p-3 lg:overflow-hidden lg:p-4">
          <div className="mt-6 flex flex-col gap-y-8 lg:flex-row lg:items-center lg:justify-between lg:gap-y-0">
            <div className="relative h-full min-w-0 grow rounded-lg border border-default bg-surface">
              <div className="flex items-center border-b border-default p-4">
                <div
                  id="activity-record-icon"
                  className={classNames(
                    `mr-3 h-10 w-10 shrink-0 rounded-full p-2.5`,
                    isForm ? `bg-inverse` : `bg-blue-600`,
                  )}
                >
                  {isForm ? (
                    <PageEdit className="h-full w-full text-inverse" />
                  ) : (
                    <Phone className="h-full w-full text-on-color" />
                  )}
                </div>
                <div className="flex flex-col justify-between">
                  <p className="text-heading-sm text-strong">
                    {selectedPerson?.name ?? (call ? toTitleCase(call.callerId) : `Unknown`)}
                    {selectedPerson?.birthDate ? `, ${getAge(selectedPerson.birthDate)}` : ``}
                  </p>
                  {recordLocationId && (
                    <p className="text-body-xs text-subtle">
                      <LazyLocationCell locationId={recordLocationId} />
                    </p>
                  )}
                  <p className="text-body-xs text-subtle">
                    <PhoneNumber number={selectedPerson?.phone} />
                  </p>
                </div>
              </div>
              {/* <div className="grid grid-cols-2 grid-rows-4 p-3">
                <div className="flex items-center py-2 text-ui-xs text-trn-900">Phone Number</div>
                <div className="flex items-center text-body-sm text-trn-900">{selectedPerson?.phone ?? ``}</div>
                <div className="flex items-center py-2 text-ui-xs text-trn-900">Channel</div>
                <div className="flex items-center  text-body-sm text-trn-900">{call?.channel ?? ``}</div>
                <div className="flex items-center py-2 text-ui-xs text-trn-900">Staff</div>
                <div className="flex items-center text-body-sm text-trn-900">{call?.staff?.name ?? ``}</div>
                <div className="flex items-center py-2 text-ui-xs text-trn-900">Caller ID</div>
                <div className="flex items-center text-body-sm text-trn-900">{call?.callerId ?? ``}</div>
              </div> */}
              <p className="absolute -top-6 mb-2 text-ui-sm">Link Activity</p>
            </div>
            {!showNewContactStep && (
              <div className="mx-auto flex h-10 w-10 shrink-0 rotate-90 items-center justify-center rounded-full border border-default bg-surface shadow-elevation-1 lg:mx-8 lg:rotate-0">
                <ArrowSmallRightIcon className="h-4 w-4 text-subtle" />
              </div>
            )}
            {selectedContact && !showNewContactStep && (
              <div className="relative flex h-full min-w-0 grow flex-col justify-between rounded-lg border border-default bg-surface lg:flex-row">
                <div className="flex items-center border-b border-default p-4">
                  <div className="mr-3 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-100 align-middle text-ui-sm capitalize">
                    <p className="text-blue-600">
                      {typeof selectedContact?.name !== `undefined`
                        ? `${selectedContact?.name?.[0]}${lastNameInitial(
                            selectedContact?.name ?? ` `,
                          )?.toLocaleUpperCase()}`
                        : ``}
                    </p>
                  </div>
                  <div className="flex flex-col justify-between">
                    <p className="text-heading-sm text-strong">
                      {selectedContact?.name}
                      {selectedContact?.birthDate ? `, ${getAge(selectedContact.birthDate)}` : ``}
                    </p>
                    <p className="text-body-xs text-subtle">
                      <LazyLocationCell locationId={selectedContact.locationId!} />
                    </p>
                  </div>
                </div>
                <span
                  className={classNames(
                    `mb-3 ml-4 mr-2 flex h-6 w-fit items-center rounded border px-1.5 text-xs font-medium not-italic leading-4 lg:absolute lg:right-0 lg:mb-0 lg:ml-0 lg:mt-2`,
                    selectedContact.patientIds ? `bg-green-100 text-green-700` : `bg-red-100 text-red-700`,
                  )}
                >
                  {selectedContact.patientIds ? `Patient Linked` : `No Patient Linked`}
                </span>
                {/* <div className="h-11 border-b border-trn-200 py-2">
                  <TagsCell
                    model="contacts"
                    modelId={selectedContact.id!}
                    workspaceId={selectedContact.workspaceId}
                    tags={selectedContact.tags}
                    shouldWrap
                    readOnly
                  />
                </div>
                <div className="grid grid-cols-2 gap-y-1.5 p-3">
                  {!!selectedContact?.cellPhone && (
                    <>
                      <div className="flex items-center text-ui-xs text-trn-900">Phone Number</div>
                      <div className="text-body-sm text-trn-900">{selectedContact?.cellPhone}</div>
                    </>
                  )}
                  {!!selectedContact?.lifecycleStage && (
                    <>
                      <div className="flex items-center text-ui-xs text-trn-900">Lifecycle Stage</div>
                      <div className="text-body-sm text-trn-900">{selectedContact?.lifecycleStage}</div>
                    </>
                  )}
                  {!!selectedContact?.leadStatus && (
                    <>
                      <div className="flex items-center text-ui-xs text-trn-900">Lead Status</div>
                      <div className="text-body-sm text-trn-900">{selectedContact?.leadStatus}</div>
                    </>
                  )}
                </div> */}
                <p className="absolute -top-6 mb-2 text-ui-sm">With Contact</p>
              </div>
            )}
            {!selectedContact && !showNewContactStep && (
              <div className="h-full min-w-0 grow rounded-lg border border-default bg-surface">
                <div className="flex h-full items-center justify-center">
                  <div className="flex flex-col justify-between">
                    <p className="text-ui-sm text-subtle">no contact matched</p>
                  </div>
                </div>
              </div>
            )}
          </div>
          {!showConfirmationStep && !showNewContactStep && (
            <>
              <div className="mt-4 bg-surface-subtle pb-6 pt-2">
                <div className="flex w-full flex-col gap-3 lg:flex-row lg:items-center lg:justify-between lg:gap-x-4">
                  <div className="w-full lg:w-auto">
                    <ControlledSearch onValueChange={onSearchValueChange} />
                  </div>
                  <Toggle
                    label="Search Workspace"
                    defaultValue={searchWorkspace}
                    handleToggle={onSearchWorkspaceToggle}
                  />
                </div>
              </div>
              <p className="mb-4 text-ui-xs text-strong">Select the contact to merge</p>
              {/* The table fills the leftover column height on desktop; stacked, there is none to fill, so give it one. */}
              {isLoading ? (
                <div className="flex h-full min-h-80 items-center justify-center lg:min-h-0">
                  <Spinner size={20} />
                </div>
              ) : (
                <div className="flex min-h-80 flex-col overflow-hidden lg:min-h-0">
                  <SimpleTable
                    key={selectedPerson?.name ?? call?.callerId ?? form?.patientName ?? `no-key`}
                    data={data}
                    columns={columns}
                    onScroll={debounce((e) => {
                      const target = e.target as HTMLDivElement
                      if (
                        searchValue !== `` &&
                        target.scrollTop > 0 &&
                        target.scrollTop + target.offsetHeight >= target.scrollHeight * 0.8
                      ) {
                        setPage((page) => page + 1)
                      }
                    }, 1000)}
                    hideFooter
                  />
                </div>
              )}
            </>
          )}
          {showConfirmationStep && !inProgress && (
            <div className="flex grow items-center justify-center">
              <div className="p-4">
                <p className="text-body-md">
                  <span className="text-heading-sm">
                    {selectedPerson?.name ?? (call ? toTitleCase(call.callerId) : `Unknown`)}
                  </span>
                  {` `}
                  will be matched with
                  {` `}
                  <span className="text-heading-sm">{selectedContact?.name}</span>
                </p>
                <div className="ml-2 mt-4 flex flex-col space-y-4 text-body-sm text-subtle">
                  <p>
                    1. The resulting contact will inherit its properties from the {isForm ? `form` : `call`} where
                    applicable.
                  </p>
                  <p>2. Using the new data from the {isForm ? `form` : `call`}, the contact will be rebuilt.</p>
                  <p>3. This may result in a change in status, dates and a variety of existing contact fields.</p>
                  <p>
                    4. Please make sure the contact you are linking the {isForm ? `form` : `call`} to is the correct
                    contact.
                  </p>
                </div>
              </div>
            </div>
          )}
          {showConfirmationStep && inProgress && (
            <div className="mx-auto my-4 flex grow flex-col items-center justify-center space-y-4">
              <CircularProgressBar progress={progress} />
              <p className="text-heading-md">{progress !== 100 ? `Matching in progress...` : `Successfully matched`}</p>
            </div>
          )}
          {showNewContactStep && !inProgress && (
            <div className="flex grow items-center justify-center">
              <div className="p-4">
                <p className="text-body-md">
                  <span className="text-heading-sm">Create a new contact</span> from the{` `}
                  <span className="text-heading-sm">Activity</span>
                </p>
                <div className="ml-2 mt-4 flex flex-col space-y-4 text-body-sm text-subtle">
                  <p>
                    1. The contact created will be based on the data from the{` `}
                    {isForm ? `form submission` : `phone call`}.
                  </p>
                  <p>2. Logic will run to set date fields, lifecycle, and lead status.</p>
                  <p>3. This may result in the contact being tagged and included in lists like "Needs Action".</p>
                  <p>4. Please make sure a contact for this individual doesn't already exist.</p>
                </div>
              </div>
            </div>
          )}
          {showNewContactStep && inProgress && (
            <div className="mx-auto my-4 flex grow flex-col items-center justify-center space-y-4">
              <CircularProgressBar progress={progress} />
              <p className="text-heading-md">{progress !== 100 ? `Creating contact...` : `Contact created`}</p>
            </div>
          )}
        </div>
        <div className="mt-auto flex justify-end space-x-2 border-t border-default px-4 py-3 lg:px-6">
          {!showConfirmationStep && !showNewContactStep ? (
            <div className="flex grow flex-col gap-2 lg:flex-row lg:items-center lg:justify-between lg:gap-0">
              <Button
                label="Create new contact"
                mode="secondary"
                size="md"
                onClick={() => setShowNewContactStep(true)}
              />
              <div className="flex justify-end gap-2 lg:space-x-2 lg:gap-0">
                <Button label="Cancel" mode="secondary" size="md" onClick={() => closeModal()} />
                <Button
                  label="Next"
                  disabled={!selectedContact || selectedContact.id === matchedContact?.id ? true : undefined}
                  mode="primary"
                  size="md"
                  onClick={() => setShowConfirmationStep(true)}
                />
              </div>
            </div>
          ) : !inProgress ? (
            <div className="flex grow items-center justify-between">
              <p className="text-heading-xs text-red-500"></p>
              <div className="flex items-center space-x-2">
                <Button
                  label="Back"
                  mode="secondary"
                  size="md"
                  onClick={() => {
                    setShowConfirmationStep(false)
                    setShowNewContactStep(false)
                  }}
                />
                <Button
                  mode="primary"
                  label="Confirm"
                  size="md"
                  onClick={() => (showConfirmationStep ? mergeContacts() : createNewContact())}
                />
              </div>
            </div>
          ) : (
            <Button
              label={currentPage === `activities` ? `Go to Activity` : `Go to Contact`}
              mode="primary"
              size="md"
              disabled={progress !== 100 ? true : undefined}
              onClick={() => (currentPage === `activities` ? goToActivity() : goToContact())}
            />
          )}
        </div>
      </div>
    </div>
  )
}
